import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/errors';
import { tokens } from '@/lib/api/tokens';
import type { Workspace } from '@/lib/api/types';
import { withSnapshot } from '@/lib/offline/snapshot';

interface WorkspaceState {
  workspace: Workspace;
  workspaces: Workspace[];
  select: (id: string) => void;
}

const Ctx = createContext<WorkspaceState | null>(null);

/** Resolves the active workspace (saved choice, else the first) and sets the X-Workspace-Id header source. */
export function WorkspaceProvider({ children, fallback }: { children: ReactNode; fallback: ReactNode }) {
  const list = useQuery({
    queryKey: ['workspaces'],
    ...withSnapshot('workspaces', async () => unwrap(await api.GET('/workspaces'))),
  });

  const saved = tokens.workspaceId();
  const workspaces = list.data ?? [];
  const active = workspaces.find((w) => w.id === saved) ?? workspaces[0];

  useEffect(() => {
    if (active && active.id !== saved) tokens.setWorkspaceId(active.id);
  }, [active, saved]);

  if (!active) return <>{fallback}</>;

  const select = (id: string) => {
    tokens.setWorkspaceId(id);
    window.location.reload(); // workspace-scoped caches are keyed by id, but a reload is the simplest safe switch
  };
  return <Ctx.Provider value={{ workspace: active, workspaces, select }}>{children}</Ctx.Provider>;
}

export function useWorkspace(): WorkspaceState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useWorkspace outside WorkspaceProvider');
  return v;
}
